/**
 * Phone-only sign-in — split-page layout.
 *
 * Two full-bleed halves at lg+:
 *   - BrandPanel (indigo gradient, decorative blur blobs, inverted
 *     GoodShowHQ logo, value-prop headline, three benefit rows, trust
 *     line at the bottom)
 *   - FormPanel (white, "← Back to home" link + mobile-only logo at
 *     the top, then the phone → code → success form vertically
 *     centered)
 *
 * On mobile the panels stack — brand on top, form below — and each
 * fills the viewport naturally rather than sitting on a colored
 * background. The form is intentionally NOT wrapped in a Card; the
 * white panel is the container.
 *
 * Firebase wiring:
 *   - Invisible RecaptchaVerifier mounted on #recaptcha-container.
 *   - Verifier is rebuilt on every "Send code" call — Firebase
 *     burns it after one challenge, and reusing it raises
 *     auth/captcha-check-failed on the second send.
 *   - sessionToken (?sessionToken=…) flows through from the
 *     desktop pairing page so post-sign-in we land on /link
 *     instead of /profile.
 */
import React, {useEffect, useRef, useState} from 'react';
import {Link as RouterLink, useNavigate, useSearchParams} from 'react-router-dom';
import {Box, Button, CircularProgress, ThemeProvider, Typography} from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import YouTubeRounded from '@mui/icons-material/YouTube';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import BoltRounded from '@mui/icons-material/BoltRounded';
import {
  ConfirmationResult,
  RecaptchaVerifier,
  onAuthStateChanged,
  signInWithPhoneNumber,
} from 'firebase/auth';
import {auth} from '../../firebase';
import {usePageTitle} from '../../hooks/usePageTitle';
import {goodShowHQTheme} from '../../theme/planapusTheme';
import {decidePostAuthRoute} from './postAuthRoute';

// -- Tokens ------------------------------------------------------------------

const SLATE = {
  50: '#F8FAFC',
  100: '#F1F5F9',
  200: '#E2E8F0',
  300: '#CBD5E1',
  400: '#94A3B8',
  500: '#64748B',
  600: '#475569',
  700: '#334155',
  900: '#0F172A',
};
const INDIGO = {
  100: '#E0E7FF',
  200: '#C7D2FE',
  300: '#A5B4FC',
  400: '#818CF8',
  600: '#4F46E5',
  700: '#4338CA',
  900: '#312E81',
};
const ROSE = {700: '#BE123C'};
const EMERALD = {100: '#D1FAE5', 600: '#059669'};
const AMBER = {300: '#FCD34D', 400: '#FBBF24'};

// -- Country list (verbatim from reference) ---------------------------------

interface Country {
  code: string;
  dial: string;
  flag: string;
  label: string;
}
const COUNTRIES: readonly Country[] = [
  {code: 'US', dial: '+1', flag: '🇺🇸', label: 'United States'},
  {code: 'CA', dial: '+1', flag: '🇨🇦', label: 'Canada'},
  {code: 'GB', dial: '+44', flag: '🇬🇧', label: 'United Kingdom'},
  {code: 'AU', dial: '+61', flag: '🇦🇺', label: 'Australia'},
  {code: 'DE', dial: '+49', flag: '🇩🇪', label: 'Germany'},
  {code: 'FR', dial: '+33', flag: '🇫🇷', label: 'France'},
  {code: 'EG', dial: '+20', flag: '🇪🇬', label: 'Egypt'},
  {code: 'AE', dial: '+971', flag: '🇦🇪', label: 'UAE'},
  {code: 'SA', dial: '+966', flag: '🇸🇦', label: 'Saudi Arabia'},
  {code: 'IN', dial: '+91', flag: '🇮🇳', label: 'India'},
];

// Pre-fill the phone field when running locally so we don't retype the
// test number on every emulator restart. Production initial state is
// empty.
const isDevelopment =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === '0.0.0.0');
const DEV_DEFAULT_PHONE = '4252420931';

// -- Error mapping -----------------------------------------------------------

function mapAuthError(err: unknown): string {
  const code =
    (err as {code?: string} | null | undefined)?.code ??
    ((err as {message?: string} | null | undefined)?.message ?? '');
  if (code.includes('auth/invalid-phone-number'))
    return "That doesn't look like a valid phone number.";
  if (code.includes('auth/too-many-requests'))
    return 'Too many tries. Wait a few minutes and try again.';
  if (code.includes('auth/invalid-verification-code'))
    return "That code didn't match. Try again or request a new one.";
  if (code.includes('auth/code-expired'))
    return 'This code has expired. Tap Resend to get a new one.';
  return 'Something went wrong. Please try again.';
}

// -- Logos -------------------------------------------------------------------

// Inverted (white square + indigo play + amber sparkle + white wordmark)
// for use on the dark BrandPanel.
function LogoOnDark(): React.JSX.Element {
  return (
    <Box
      component={RouterLink}
      to="/"
      aria-label="GoodShowHQ home"
      sx={{display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none'}}
    >
      <Box
        component="svg"
        viewBox="0 0 64 64"
        sx={{width: 32, height: 32}}
        aria-hidden="true"
      >
        <rect width="64" height="64" rx="18" fill="#FFFFFF" />
        <path d="M25 20 L25 44 L45 32 Z" fill="#3448F0" />
        <g transform="translate(52 14)">
          <path
            d="M0 -7 C1.8 -1.8 1.8 -1.8 7 0 C1.8 1.8 1.8 1.8 0 7 C-1.8 1.8 -1.8 1.8 -7 0 C-1.8 -1.8 -1.8 -1.8 0 -7 Z"
            fill="#FFD166"
          />
        </g>
      </Box>
      <Typography
        component="span"
        sx={{fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em', color: '#FFFFFF'}}
      >
        GoodShowHQ
      </Typography>
    </Box>
  );
}

// Standard (gradient square + white play + amber sparkle + slate
// wordmark) for use on the white FormPanel header at mobile width.
function LogoDark({small = false}: {small?: boolean}): React.JSX.Element {
  const sz = small ? 28 : 32;
  return (
    <Box
      component={RouterLink}
      to="/"
      aria-label="GoodShowHQ home"
      sx={{display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none'}}
    >
      <Box
        component="svg"
        viewBox="0 0 64 64"
        sx={{width: sz, height: sz}}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="kt-auth-d" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6366F1" />
            <stop offset="100%" stopColor="#3448F0" />
          </linearGradient>
        </defs>
        <rect width="64" height="64" rx="18" fill="url(#kt-auth-d)" />
        <path d="M25 20 L25 44 L45 32 Z" fill="#FFFFFF" />
        <g transform="translate(52 14)">
          <path
            d="M0 -7 C1.8 -1.8 1.8 -1.8 7 0 C1.8 1.8 1.8 1.8 0 7 C-1.8 1.8 -1.8 1.8 -7 0 C-1.8 -1.8 -1.8 -1.8 0 -7 Z"
            fill="#FFD166"
          />
        </g>
      </Box>
      <Typography
        component="span"
        sx={{fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em', color: SLATE[900]}}
      >
        GoodShowHQ
      </Typography>
    </Box>
  );
}

// -- Brand panel -------------------------------------------------------------

interface Benefit {
  Icon: React.ElementType;
  title: string;
  body: string;
}
const BENEFITS: readonly Benefit[] = [
  {
    Icon: YouTubeRounded,
    title: "Link your kid's YouTube",
    body: 'One-time setup. Nothing for them to install.',
  },
  {
    Icon: AutoAwesomeRounded,
    title: 'AI-reviewed scores on every video',
    body: 'Educational depth, age fit, pacing, creator credibility.',
  },
  {
    Icon: BoltRounded,
    title: 'Take action in one tap',
    body: 'Block junk channels, boost the great ones, override anything.',
  },
];

function BrandPanel(): React.JSX.Element {
  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: `linear-gradient(to bottom right, ${INDIGO[600]} 0%, ${INDIGO[700]} 50%, ${INDIGO[900]} 100%)`,
        color: '#FFFFFF',
        px: {xs: 4, lg: 8},
        py: {xs: 5, lg: 7},
      }}
    >
      {/* Decorative blobs — positioned BEFORE the content stack so
          they sit behind it. pointerEvents: none so they don't
          intercept clicks. */}
      <Box
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          top: '-8rem',
          left: '-8rem',
          width: '24rem',
          height: '24rem',
          borderRadius: '50%',
          bgcolor: INDIGO[400],
          opacity: 0.25,
          filter: 'blur(64px)',
        }}
      />
      <Box
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          right: '-10rem',
          bottom: 0,
          width: '28rem',
          height: '28rem',
          borderRadius: '50%',
          bgcolor: AMBER[400],
          opacity: 0.10,
          filter: 'blur(64px)',
        }}
      />
      <Box
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          right: '4rem',
          top: '5rem',
          width: '8rem',
          height: '8rem',
          borderRadius: '50%',
          bgcolor: INDIGO[300],
          opacity: 0.20,
          filter: 'blur(48px)',
          display: {xs: 'none', lg: 'block'},
        }}
      />

      {/* Content stack — relative-positioned so it stacks above
          the absolutely-positioned blobs above. */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
        }}
      >
        <LogoOnDark />

        <Box sx={{my: 'auto', maxWidth: 480, py: 5}}>
          <Typography
            component="h1"
            sx={{
              fontSize: {xs: 36, lg: 48},
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: '#FFFFFF',
            }}
          >
            Five minutes from a calmer YouTube.
          </Typography>
          <Typography
            sx={{
              mt: 2.5,
              fontSize: 16,
              lineHeight: 1.65,
              color: INDIGO[100],
            }}
          >
            GoodShowHQ reviews every video before it reaches your kid's feed —
            using your priorities, not YouTube's algorithm.
          </Typography>

          <Box component="ul" sx={{mt: 5, p: 0, m: 0, listStyle: 'none'}}>
            {BENEFITS.map(({Icon, title, body}) => (
              <Box
                component="li"
                key={title}
                sx={{
                  display: 'flex',
                  gap: 1.75,
                  mb: 2.5,
                  '&:last-of-type': {mb: 0},
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.10)',
                    backdropFilter: 'blur(8px)',
                    color: AMBER[300],
                  }}
                >
                  <Icon sx={{fontSize: 18}} />
                </Box>
                <Box>
                  <Typography
                    component="h3"
                    sx={{fontSize: 14, fontWeight: 700, color: '#FFFFFF', m: 0}}
                  >
                    {title}
                  </Typography>
                  <Typography
                    sx={{mt: 0.25, fontSize: 14, lineHeight: 1.6, color: INDIGO[100]}}
                  >
                    {body}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography sx={{fontSize: 12, color: INDIGO[200]}}>
          COPPA-compliant · Phone-only sign-in · We don't sell your family's data.
        </Typography>
      </Box>
    </Box>
  );
}

// -- Country picker ----------------------------------------------------------

function CountryPicker({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const country = COUNTRIES.find(c => c.code === value) ?? COUNTRIES[0];
  const ref = useRef<HTMLDivElement | null>(null);

  // Click-outside dismiss — the dropdown is portal-free so we close
  // it ourselves rather than relying on MUI's Menu.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <Box ref={ref} sx={{position: 'relative'}}>
      <Button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        sx={{
          height: 48,
          minWidth: 0,
          gap: 0.75,
          px: 1.5,
          borderRadius: '12px 0 0 12px',
          border: `1px solid ${SLATE[200]}`,
          borderRight: 'none',
          bgcolor: '#FFFFFF',
          fontSize: 14,
          fontWeight: 600,
          color: SLATE[700],
          textTransform: 'none',
          '&:hover': {bgcolor: SLATE[50], borderColor: SLATE[200]},
        }}
      >
        <Box component="span" sx={{fontSize: 16, lineHeight: 1}}>
          {country.flag}
        </Box>
        <Box component="span" sx={{fontVariantNumeric: 'tabular-nums'}}>
          {country.dial}
        </Box>
        <ExpandMoreRounded sx={{fontSize: 14, color: SLATE[400]}} />
      </Button>
      {open && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: '100%',
            zIndex: 10,
            mt: 0.5,
            maxHeight: 256,
            width: 256,
            overflowY: 'auto',
            borderRadius: 2,
            border: `1px solid ${SLATE[200]}`,
            bgcolor: '#FFFFFF',
            py: 0.5,
            boxShadow: '0 10px 25px rgba(15,23,42,0.10)',
          }}
        >
          {COUNTRIES.map(c => (
            <Box
              key={c.code}
              component="button"
              type="button"
              onClick={() => {
                onChange(c.code);
                setOpen(false);
              }}
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 1,
                textAlign: 'left',
                fontSize: 14,
                bgcolor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: SLATE[700],
                '&:hover': {bgcolor: SLATE[50]},
              }}
            >
              <Box component="span" sx={{fontSize: 16}}>
                {c.flag}
              </Box>
              <Box
                component="span"
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {c.label}
              </Box>
              <Box
                component="span"
                sx={{fontFamily: 'monospace', fontSize: 12, color: SLATE[400]}}
              >
                {c.dial}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// -- Form views --------------------------------------------------------------

interface PhoneViewProps {
  country: string;
  setCountry: (c: string) => void;
  phone: string;
  setPhone: (p: string) => void;
  onSubmit: () => void;
  sending: boolean;
  error: string;
}

function PhoneView({
  country,
  setCountry,
  phone,
  setPhone,
  onSubmit,
  sending,
  error,
}: PhoneViewProps): React.JSX.Element {
  return (
    <Box>
      <Typography
        component="h2"
        sx={{fontSize: 24, fontWeight: 800, letterSpacing: '-0.01em', color: SLATE[900], lineHeight: 1.2}}
      >
        Get started
      </Typography>
      <Typography sx={{mt: 1, fontSize: 14, color: SLATE[500]}}>
        We'll text you a 6-digit code. New here? An account is created automatically.
      </Typography>

      <Box
        component="form"
        sx={{mt: 3.5}}
        onSubmit={e => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Typography
          component="label"
          sx={{
            mb: 0.75,
            display: 'block',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: SLATE[500],
          }}
        >
          Phone number
        </Typography>
        <Box sx={{display: 'flex'}}>
          <CountryPicker value={country} onChange={setCountry} disabled={sending} />
          <Box
            component="input"
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            required
            autoFocus
            placeholder="555 123 4567"
            value={phone}
            disabled={sending}
            onChange={e => setPhone(e.target.value.replace(/[^\d\s-]/g, ''))}
            sx={{
              height: 48,
              flex: 1,
              minWidth: 0,
              borderRadius: '0 12px 12px 0',
              border: `1px solid ${SLATE[200]}`,
              bgcolor: '#FFFFFF',
              px: 2,
              fontSize: 16,
              fontWeight: 500,
              letterSpacing: 0.4,
              color: SLATE[900],
              outline: 'none',
              fontFamily: 'inherit',
              '&::placeholder': {color: SLATE[400]},
              '&:focus': {
                borderColor: '#3448F0',
                boxShadow: `0 0 0 3px ${INDIGO[100]}`,
              },
            }}
          />
        </Box>
        {error && (
          <Typography sx={{mt: 1, fontSize: 12, fontWeight: 500, color: ROSE[700]}}>
            {error}
          </Typography>
        )}

        <Button
          type="submit"
          disabled={sending || !phone.trim()}
          sx={{
            mt: 2.5,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            borderRadius: 999,
            bgcolor: '#3448F0',
            color: '#FFFFFF',
            px: 2,
            py: 1.5,
            fontSize: 14,
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': {bgcolor: '#2839C9'},
            '&.Mui-disabled': {bgcolor: '#3448F0', color: '#FFFFFF', opacity: 0.5},
          }}
        >
          {sending ? (
            <>
              <CircularProgress size={16} thickness={5} sx={{color: '#FFFFFF'}} />
              Sending code…
            </>
          ) : (
            'Send code'
          )}
        </Button>
      </Box>

      <Typography
        sx={{mt: 3, fontSize: 12, lineHeight: 1.6, color: SLATE[400]}}
      >
        Standard message rates may apply. By continuing, you agree to GoodShowHQ's{' '}
        <Box
          component="a"
          href="#"
          sx={{fontWeight: 600, color: SLATE[600], textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}
        >
          Terms
        </Box>{' '}
        and{' '}
        <Box
          component="a"
          href="#"
          sx={{fontWeight: 600, color: SLATE[600], textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}
        >
          Privacy Policy
        </Box>
        .
      </Typography>

      {/* Invisible reCAPTCHA. Must exist in the DOM before
          RecaptchaVerifier is constructed. */}
      <Box id="recaptcha-container" />
    </Box>
  );
}

interface CodeViewProps {
  phoneDisplay: string;
  code: string;
  setCode: (c: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  verifying: boolean;
  error: string;
  resendIn: number;
  onResend: () => void;
}

function CodeView({
  phoneDisplay,
  code,
  setCode,
  onBack,
  onSubmit,
  verifying,
  error,
  resendIn,
  onResend,
}: CodeViewProps): React.JSX.Element {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  const handleDigit = (i: number, v: string) => {
    const digit = v.replace(/\D/g, '').slice(-1);
    const next = code.split('');
    next[i] = digit;
    while (next.length < 6) next.push('');
    setCode(next.join('').slice(0, 6));
    if (digit && i < 5) inputs.current[i + 1]?.focus();
  };

  const handleKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[i] && i > 0) {
      inputs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const pasted = (e.clipboardData?.getData('text') ?? '')
      .replace(/\D/g, '')
      .slice(0, 6);
    if (pasted.length) {
      e.preventDefault();
      setCode(pasted);
      inputs.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  // Auto-submit when the 6th digit is entered. The lint warning
  // about missing deps is intentional — `onSubmit` captures fresh
  // state via the parent's render cycle, and adding it to deps
  // re-fires verification on every render.
  useEffect(() => {
    if (code.length === 6 && !verifying) onSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <Box>
      <Box
        component="button"
        type="button"
        onClick={onBack}
        sx={{
          mb: 2,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: 12,
          fontWeight: 600,
          color: SLATE[500],
          bgcolor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          '&:hover': {color: SLATE[900]},
        }}
      >
        <ArrowBackRounded sx={{fontSize: 14}} />
        Back
      </Box>
      <Typography
        component="h2"
        sx={{fontSize: 24, fontWeight: 800, letterSpacing: '-0.01em', color: SLATE[900], lineHeight: 1.2}}
      >
        Enter the code
      </Typography>
      <Typography sx={{mt: 1, fontSize: 14, color: SLATE[500]}}>
        We sent a 6-digit code to{' '}
        <Box component="span" sx={{fontWeight: 600, color: SLATE[900]}}>
          {phoneDisplay}
        </Box>
        .
      </Typography>

      <Box
        component="form"
        sx={{mt: 3.5}}
        onSubmit={e => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Box
          sx={{display: 'flex', justifyContent: 'space-between', gap: 1}}
          onPaste={handlePaste}
        >
          {[0, 1, 2, 3, 4, 5].map(i => (
            <Box
              key={i}
              component="input"
              ref={(el: HTMLInputElement | null) => {
                inputs.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={code[i] ?? ''}
              disabled={verifying}
              onChange={e => handleDigit(i, e.target.value)}
              onKeyDown={e => handleKey(i, e as React.KeyboardEvent<HTMLInputElement>)}
              autoFocus={i === 0}
              sx={{
                height: 56,
                width: '100%',
                maxWidth: '3.25rem',
                borderRadius: 2,
                border: `1px solid ${SLATE[200]}`,
                bgcolor: '#FFFFFF',
                textAlign: 'center',
                fontSize: 24,
                fontWeight: 700,
                color: SLATE[900],
                outline: 'none',
                fontFamily: 'inherit',
                '&:focus': {
                  borderColor: '#3448F0',
                  boxShadow: `0 0 0 3px ${INDIGO[100]}`,
                },
              }}
            />
          ))}
        </Box>

        {error && (
          <Typography
            sx={{mt: 1.5, textAlign: 'center', fontSize: 12, fontWeight: 500, color: ROSE[700]}}
          >
            {error}
          </Typography>
        )}

        <Button
          type="submit"
          disabled={verifying || code.length < 6}
          sx={{
            mt: 2.5,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            borderRadius: 999,
            bgcolor: '#3448F0',
            color: '#FFFFFF',
            px: 2,
            py: 1.5,
            fontSize: 14,
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': {bgcolor: '#2839C9'},
            '&.Mui-disabled': {bgcolor: '#3448F0', color: '#FFFFFF', opacity: 0.5},
          }}
        >
          {verifying ? (
            <>
              <CircularProgress size={16} thickness={5} sx={{color: '#FFFFFF'}} />
              Verifying…
            </>
          ) : (
            'Verify'
          )}
        </Button>
      </Box>

      <Box sx={{mt: 2.5, textAlign: 'center', fontSize: 12, color: SLATE[500]}}>
        Didn't get a code?{' '}
        {resendIn > 0 ? (
          <Box component="span" sx={{color: SLATE[400]}}>
            Resend in {resendIn}s
          </Box>
        ) : (
          <Box
            component="button"
            type="button"
            onClick={onResend}
            sx={{
              fontWeight: 600,
              color: '#3448F0',
              bgcolor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              fontSize: 12,
              '&:hover': {color: '#2839C9'},
            }}
          >
            Resend
          </Box>
        )}
      </Box>
    </Box>
  );
}

function SuccessView(): React.JSX.Element {
  return (
    <Box sx={{textAlign: 'center'}}>
      <Box
        sx={{
          mx: 'auto',
          mb: 2.5,
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: EMERALD[100],
          color: EMERALD[600],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CheckCircleRounded sx={{fontSize: 28}} />
      </Box>
      <Typography
        component="h2"
        sx={{fontSize: 24, fontWeight: 800, letterSpacing: '-0.01em', color: SLATE[900], lineHeight: 1.2}}
      >
        You're in
      </Typography>
      <Typography sx={{mx: 'auto', mt: 1, maxWidth: 384, fontSize: 14, color: SLATE[500]}}>
        Taking you to your profiles…
      </Typography>
    </Box>
  );
}

// -- Form panel --------------------------------------------------------------

type Mode = 'phone' | 'code' | 'success';

interface FormPanelProps {
  mode: Mode;
  phoneProps: PhoneViewProps;
  codeProps: CodeViewProps;
}

function FormPanel({mode, phoneProps, codeProps}: FormPanelProps): React.JSX.Element {
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FFFFFF',
        px: {xs: 4, lg: 8},
        py: {xs: 5, lg: 7},
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: {xs: 'space-between', lg: 'flex-end'},
        }}
      >
        <Box sx={{display: {xs: 'block', lg: 'none'}}}>
          <LogoDark small />
        </Box>
        <Box
          component={RouterLink}
          to="/"
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: SLATE[400],
            textDecoration: 'none',
            '&:hover': {color: SLATE[700]},
          }}
        >
          ← Back to home
        </Box>
      </Box>

      <Box sx={{mx: 'auto', my: 'auto', width: '100%', maxWidth: 384, py: 5}}>
        {mode === 'phone' && <PhoneView {...phoneProps} />}
        {mode === 'code' && <CodeView {...codeProps} />}
        {mode === 'success' && <SuccessView />}
      </Box>
    </Box>
  );
}

// -- Page --------------------------------------------------------------------

function AuthPageBody(): React.JSX.Element {
  usePageTitle('Sign in');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Desktop pairing: when the desktop app sends the user here with a
  // `?sessionToken=` param, we route to /link after sign-in instead of
  // dropping them on /profile.
  const sessionToken = searchParams.get('sessionToken');
  // Device-pairing (desktop/HA parent login): after sign-in, return to
  // /pair to claim this code.
  const pairCode = searchParams.get('pair');

  const [mode, setMode] = useState<Mode>('phone');
  const [country, setCountry] = useState<string>('US');
  const [phone, setPhone] = useState<string>(isDevelopment ? DEV_DEFAULT_PHONE : '');
  const [code, setCode] = useState<string>('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string>('');
  const [resendIn, setResendIn] = useState(0);
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null);
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);

  const dial = COUNTRIES.find(c => c.code === country)?.dial ?? '+1';
  const phoneDigits = phone.replace(/\D/g, '');
  const phoneDisplay = `${dial} ${phone}`.trim();

  // Already-signed-in fast path. Routes through `decidePostAuthRoute`
  // so a returning parent who never linked a profile lands on
  // /welcome instead of /profile (and a returning parent who already
  // completed or skipped the flow goes straight to /profile).
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async u => {
      if (!u) return;
      const target = await decidePostAuthRoute(sessionToken, pairCode);
      navigate(target, {replace: true});
    });
    return () => unsub();
  }, [navigate, sessionToken, pairCode]);

  // Resend countdown — ticks down once per second while > 0.
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  // Cleanup the verifier on unmount.
  useEffect(() => {
    return () => {
      if (recaptchaRef.current) {
        try {
          recaptchaRef.current.clear();
        } catch {
          // ignore
        }
        recaptchaRef.current = null;
      }
    };
  }, []);

  const sendCode = async () => {
    setError('');
    if (!phoneDigits) {
      setError("That doesn't look like a valid phone number.");
      return;
    }
    setSending(true);
    try {
      // Burn any prior verifier — Firebase only honors a fresh one
      // per challenge. Reusing the same verifier across two send
      // calls raises auth/captcha-check-failed on the second.
      if (recaptchaRef.current) {
        try {
          recaptchaRef.current.clear();
        } catch {
          // ignore
        }
        recaptchaRef.current = null;
      }
      recaptchaRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
      });
      const fullPhone = `${dial}${phoneDigits}`;
      const result = await signInWithPhoneNumber(auth, fullPhone, recaptchaRef.current);
      setConfirmation(result);
      setMode('code');
      setResendIn(30);
    } catch (err) {
      console.error('signInWithPhoneNumber failed:', err);
      setError(mapAuthError(err));
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async () => {
    if (!confirmation) {
      setError('Something went wrong. Please try again.');
      return;
    }
    setError('');
    setVerifying(true);
    try {
      await confirmation.confirm(code);
      setMode('success');
      // Hold the success state long enough for the user to see it,
      // then route. The onAuthStateChanged effect above will also
      // trigger a redirect; this timeout makes the transition feel
      // intentional rather than a flash.
      setTimeout(async () => {
        const target = await decidePostAuthRoute(sessionToken, pairCode);
        navigate(target, {replace: true});
      }, 600);
    } catch (err) {
      console.error('confirmationResult.confirm failed:', err);
      setError(mapAuthError(err));
      setVerifying(false);
    }
  };

  const handleResend = () => {
    setCode('');
    setError('');
    sendCode();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: {xs: 'column', lg: 'row'},
        color: SLATE[900],
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <BrandPanel />
      <FormPanel
        mode={mode}
        phoneProps={{
          country,
          setCountry,
          phone,
          setPhone,
          onSubmit: sendCode,
          sending,
          error,
        }}
        codeProps={{
          phoneDisplay,
          code,
          setCode,
          onBack: () => {
            setMode('phone');
            setCode('');
            setError('');
          },
          onSubmit: verifyCode,
          verifying,
          error,
          resendIn,
          onResend: handleResend,
        }}
      />
    </Box>
  );
}

export default function AuthPage(): React.JSX.Element {
  return (
    <ThemeProvider theme={goodShowHQTheme}>
      <CssBaseline />
      <AuthPageBody />
    </ThemeProvider>
  );
}
